import React, { useCallback, useEffect, useMemo } from 'react'
import { Form, Row, Spin } from 'antd'
import { isEmpty } from 'lodash'

import {
  DirtyPromptForm,
  FormButtons,
  FormImageUpload,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'

import { useUpdateDietImageDetailsMutation } from '../../hooks'
import { dietPropType } from '../../prop-types'

import { dietImageFormInitialValues } from './initial-values'

const DietImageDetailsForm = ({ diet }) => {
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DIET.UPDATE_IMAGE_DETAILS,
  ])

  const [
    updateDietImageDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDietImageDetailsMutation()

  const initialValues = useMemo(() => dietImageFormInitialValues(diet), [diet])

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const handleSaveData = useCallback(
    data => updateDietImageDetails(diet.id, data),
    [diet.id, updateDietImageDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Diet details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [diet.imageUrl, diet.onboardingImageUrl, resetForm])

  return isSaveLoading || isEmpty(initialValues) ? (
    <Spin />
  ) : (
    <>
      <DirtyPromptForm
        isDirty={isDirty}
        form={form}
        initialValues={initialValues}
        name="dietImageForm"
        onFinish={handleSaveData}
        autoComplete="off"
        onFieldsChange={setToDirty}
      >
        <Row>
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="image"
            label="Image"
          />

          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="onboardingImage"
            label="Onboarding Image"
          />
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.DIET.UPDATE_IMAGE_DETAILS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </DirtyPromptForm>
    </>
  )
}

DietImageDetailsForm.propTypes = {
  diet: dietPropType.isRequired,
}

export default DietImageDetailsForm
